import { apiFetch, apiUpload, apiDownload, apiDownloadNamed, type IdResponse, type OperationResult } from "@/lib/apiClient";
import { saveBlob } from "@/utils/download";
import { fetchAll, pageQuery, toPaged, type Paged, type PageParams } from "@/lib/paging";
import { odata } from "@/lib/odata";
import { userRef, nameRef, toUserRef, type UserRef } from "@/utils/userRef";
import { canonicalOwnerTitle, canonicalOwnershipType } from "@/features/applications/components/v2/constants";

interface AppListResponse {
  id: string;
  dbaName: string;
  legalName: string | null;
  status: string;
  userId: string | null;
  wizardStep: number;
  creationTime: string;
  ownerName: string | null;
  associateName: string | null;
}

function toAppListItem(r: AppListResponse): ApplicationListItem {
  return {
    id: r.id,
    dbaName: r.dbaName,
    legalName: r.legalName ?? null,
    status: r.status,
    submittedAt: r.creationTime,
    wizardStep: r.wizardStep ?? 0,
    owner: userRef(r.userId, r.ownerName),
    associate: nameRef(r.associateName),
  };
}

function toAppDetail(r: Record<string, unknown>): ApplicationDetail {
  return {
    ...(r as object),
    submittedAt: (r.creationTime as string) ?? "",
    owner: userRef(r.userId as string | null),
    associate: userRef(r.associateUserId as string | null),
    externalId: null,
    userNumber: null,
    dbaSameAsLegal: (r.dbaSameAsLegal as boolean) ?? false,
    wizardStep: (r.wizardStep as number) ?? 0,
  } as unknown as ApplicationDetail;
}

interface AppV2ListResponse {
  id: string;
  dbaName: string | null;
  legalName: string | null;
  status: string;
  source: string;
  wizardStep: number;
  mcc: string | null;
  merchantId: string | null;
  owner: unknown;
  associate: unknown;
  principalCount: number | null;
  documentCount: number | null;
  createdAt: string;
  updatedAt: string | null;
  submittedAt: string | null;
  statusSince?: string | null;
  processorMid?: string | null;
  rateApprovalPending?: boolean;
}

function toAppV2ListItem(r: AppV2ListResponse): ApplicationV2ListItem {
  return {
    id: r.id,
    status: r.status,
    wizardStep: r.wizardStep ?? 0,
    source: r.source,
    dbaName: r.dbaName ?? null,
    legalName: r.legalName ?? null,
    mcc: r.mcc ?? null,
    owner: r.owner ? toUserRef(r.owner) : null,
    associate: r.associate ? toUserRef(r.associate) : null,
    merchantId: r.merchantId ?? null,
    principalCount: r.principalCount ?? 0,
    documentCount: r.documentCount ?? 0,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt ?? r.createdAt,
    submittedAt: r.submittedAt ?? null,
    statusSince: r.statusSince ?? null,
    processorMid: r.processorMid ?? null,
    rateApprovalPending: r.rateApprovalPending === true,
  };
}

interface CampaignListResponse {
  id: string;
  name: string;
  description: string | null;
  campaignType: string;
  status: string;
  isPublic: boolean;
  creationTime: string;
  acquirer: string | null;
}

function toCampaignListItem(r: CampaignListResponse): CampaignListItem {
  return {
    id: r.id,
    name: r.name,
    status: r.status,
    campaignType: r.campaignType,
    isPublic: r.isPublic ?? false,
    acquirer: r.acquirer ?? null,
    updatedAt: r.creationTime,
  };
}

function toCampaignDetail(r: Record<string, unknown>): CampaignDetail {
  const str = (k: string) => (r[k] as string) ?? null;
  const numField = (k: string) => (r[k] as number) ?? null;
  return {
    id: (r.id as string) ?? "",
    name: (r.name as string) ?? "",
    description: str("description"),
    isPublic: (r.isPublic as boolean) ?? false,
    campaignType: (r.campaignType as string) ?? "",
    acquirer: str("acquirer"),
    boardingApplicationType: (r.boardingApplicationType as string) ?? "",
    giactKbaEnabled: (r.giactKbaEnabled as boolean) ?? false,
    status: (r.status as string) ?? "",
    acquiringFeeSpecialNote: str("acquiringFeeSpecialNote"),
    achFeeSpecialNote: str("achFeeSpecialNote"),
    tsysAssociation: str("tsysAssociation"),
    leadSource: str("leadSource"),
    passThroughInterchangePlus: numField("passThroughInterchangePlus"),
    pinDebitRate: numField("pinDebitRate"),
    ebtRate: numField("ebtRate"),
    transactionFee: numField("transactionFee"),
    authorizationFee: numField("authorizationFee"),
    avsFee: numField("avsFee"),
    voiceAuthorizationFee: numField("voiceAuthorizationFee"),
    pinDebitAuthorizationFee: numField("pinDebitAuthorizationFee"),
    ebtAuthorizationFee: numField("ebtAuthorizationFee"),
    monthlyDashboardAccessFee: numField("monthlyDashboardAccessFee"),
    monthlyPciFee: numField("monthlyPciFee"),
    monthlyMinimumFee: numField("monthlyMinimumFee"),
    monthlyFee: numField("monthlyFee"),
    applicationFee: numField("applicationFee"),
    annualFee: numField("annualFee"),
    batchFee: numField("batchFee"),
    chargebackFee: numField("chargebackFee"),
    retrievalFee: numField("retrievalFee"),
    qualifiedRate: numField("qualifiedRate"),
    midQualSurcharge: numField("midQualSurcharge"),
    nonQualSurcharge: numField("nonQualSurcharge"),
    visaMcDiscoverRate: numField("visaMcDiscoverRate"),
    americanExpressOptBlueRate: numField("americanExpressOptBlueRate"),
    offlineDebitRate: numField("offlineDebitRate"),
    offlineDebitPassThrough: numField("offlineDebitPassThrough"),
    pinDebitRatePassThrough: numField("pinDebitRatePassThrough"),
    gatewayFee: numField("gatewayFee"),
    wirelessFee: numField("wirelessFee"),
    posSystemFee: numField("posSystemFee"),
    perTransFee: numField("perTransFee"),
    setupFee: numField("setupFee"),
    serviceProviderMonthlyFee: numField("serviceProviderMonthlyFee"),
    otherServiceProviderFee: numField("otherServiceProviderFee"),
    createdAt: (r.creationTime as string) ?? "",
    updatedAt: (r.creationTime as string) ?? "",
  };
}

export interface ApplicationListItem {
  id: string;
  dbaName: string;
  legalName: string | null;
  status: string;
  submittedAt: string;
  wizardStep: number;
  owner: UserRef | null;
  associate: UserRef | null;
}

export interface ApplicationDetail {
  id: string;
  externalId: string | null;
  submittedAt: string;
  source: string | null;
  userNumber: number | null;
  dbaName: string;
  legalName: string | null;
  mcc: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  taxId: string | null;
  ein: string | null;
  ssn: string | null;
  bankAccountNumber: string | null;
  bankRoutingNumber: string | null;
  status: string;
  notes: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  wizardStep: number;

  website: string | null;
  ownershipType: string | null;
  businessStartDate: string | null;
  dbaSameAsLegal: boolean;
  dbaAddressLine1: string | null;
  dbaCity: string | null;
  dbaState: string | null;
  dbaZip: string | null;
  averageMonthlyVolume: number | null;
  averageTransactionAmount: number | null;
  maxTransactionAmount: number | null;
  productDescription: string | null;
  ownerFirstName: string | null;
  ownerLastName: string | null;
  ownerTitle: string | null;
  ownerDateOfBirth: string | null;
  ownerSsn: string | null;
  ownerPhone: string | null;
  ownerHomeAddressLine1: string | null;
  ownerHomeCity: string | null;
  ownerHomeState: string | null;
  ownerHomeZip: string | null;
  bankName: string | null;
  depositAccountType: string | null;
  owner: UserRef | null;
  associate: UserRef | null;
}

export const applications = {
  async list(params: PageParams = {}): Promise<ApplicationListItem[]> {
    const res = await apiFetch<unknown>(`/applications${pageQuery({ pageSize: 200, ...params })}`);
    return toPaged<AppListResponse>(res as AppListResponse[]).items.map(toAppListItem);
  },
  async get(id: string) {
    const r = await apiFetch<Record<string, unknown>>(`/applications/${encodeURIComponent(id)}`);
    return toAppDetail(r);
  },
};

export interface PublicApplicationRequest {
  dbaName: string;
  legalName?: string | null;
  mcc?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  taxId?: string | null;
  ein?: string | null;
  notes?: string | null;
  referringUserNumber?: number | null;

  agentCode?: string | null;
  pricedLinkCode?: string | null;
  campaignId?: string | null;

  website?: string | null;
  ownershipType?: string | null;
  businessStartDate?: string | null;
  dbaSameAsLegal?: boolean;
  dbaAddressLine1?: string | null;
  dbaCity?: string | null;
  dbaState?: string | null;
  dbaZip?: string | null;
  averageMonthlyVolume?: number | null;
  averageTransactionAmount?: number | null;
  maxTransactionAmount?: number | null;
  productDescription?: string | null;
  ownerFirstName?: string | null;
  ownerMiddleName?: string | null;
  ownerLastName?: string | null;
  ownerTitle?: string | null;
  ownerDateOfBirth?: string | null;
  ownerIsUsCitizen?: boolean | null;
  ownerSsn?: string | null;
  ownerPhone?: string | null;
  ownerHomeAddressLine1?: string | null;
  ownerHomeCity?: string | null;
  ownerHomeState?: string | null;
  ownerHomeZip?: string | null;
  bankName?: string | null;
  depositAccountType?: string | null;
  ssn?: string | null;
  bankRoutingNumber?: string | null;
  bankAccountNumber?: string | null;
  separateWithdrawAccount?: boolean | null;
  withdrawBankName?: string | null;
  withdrawAccountType?: string | null;
  withdrawRoutingNumber?: string | null;
  ownerPassportNumber?: string | null;
  ownerPassportCountry?: string | null;
  withdrawAccountNumber?: string | null;

  driverLicenseImageBase64?: string | null;
  driverLicenseFileName?: string | null;
  driverLicenseContentType?: string | null;
  voidedCheckImageBase64?: string | null;
  voidedCheckFileName?: string | null;
  voidedCheckContentType?: string | null;
}

export interface AlreadyAppliedCheck {
  taxId: string | null;
  email: string | null;
  phone: string | null;
}

export interface AlreadyAppliedResult {
  matched: boolean;
  submittedOn: string | null;
}

export const publicApplications = {
  alreadyApplied(req: AlreadyAppliedCheck) {
    return apiFetch<AlreadyAppliedResult>("/public/applications/already-applied", {
      method: "POST",
      body: JSON.stringify(req),
    });
  },

  confirmSame(req: AlreadyAppliedCheck) {
    return apiFetch<OperationResult>("/public/applications/already-applied/same", {
      method: "POST",
      body: JSON.stringify(req),
    });
  },

  replacesEarlier(id: string) {
    return apiFetch<OperationResult>(`/public/applications/${encodeURIComponent(id)}/replaces-earlier`, { method: "POST" });
  },

  submit(req: PublicApplicationRequest) {
    return apiFetch<IdResponse>("/public/applications", {
      method: "POST",
      body: JSON.stringify(req),
    });
  },

  submitVerification(req: PublicApplicationRequest) {
    return apiFetch<{ verified: boolean; businessName: string | null; ein: string | null }>(
      "/public/business-verification",
      { method: "POST", body: JSON.stringify(req) },
    );
  },
};

export type V2Value = string | number | boolean | string[] | null;

export type V2Bag = Record<string, V2Value>;

export interface ApplicationV2ListItem {
  id: string;
  status: string;
  wizardStep: number;
  source: string;
  dbaName: string | null;
  legalName: string | null;
  mcc: string | null;
  owner: UserRef | null;
  associate: UserRef | null;
  merchantId: string | null;
  principalCount: number;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  statusSince: string | null;
  processorMid: string | null;
  rateApprovalPending: boolean;
}

export interface ApplicationDocumentV2 {
  id: string;
  documentType: string;
  filename: string;
  filePath: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedById: string | null;
  fromMerchant?: boolean;
}

export interface ApplicationV2Detail {
  id: string;
  status: string;
  wizardStep: number;
  source: string;
  owner: UserRef | null;
  associate: UserRef | null;
  merchantId: string | null;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  statusSince: string | null;
  processorMid: string | null;
  underwritingNote: string | null;
  underwritingRequested: boolean;
  viewerIsBackOffice: boolean | null;
  pricingRequired: boolean;
  fields: V2Bag;
  principals: V2Bag[];
  bankAccounts: V2Bag[];
  posSetup: V2Bag[];
  tradeReferences: V2Bag[];
  feeSchedule: V2Bag | null;
  documents: ApplicationDocumentV2[];
}

export interface SaveApplicationV2Request {
  wizardStep: number;
  associateUserId?: string | null;
  fields?: V2Bag | null;
  principals?: V2Bag[] | null;
  bankAccounts?: V2Bag[] | null;
  posSetup?: V2Bag[] | null;
  tradeReferences?: V2Bag[] | null;
  feeSchedule?: V2Bag | null;
}

const APP_V2_META_KEYS = new Set([
  "id", "status", "wizardStep", "source", "userId", "associateUserId",
  "owner", "associate", "merchantId", "submittedAt", "pricingRequired", "statusSince", "processorMid", "underwritingNote",
  "underwritingRequested", "viewerIsBackOffice",
  "createdAt", "updatedAt", "creationTime", "lastModificationTime",
  "principals", "bankAccounts", "tradeReferences", "posSetup", "feeSchedule",
  "documents",
]);

function toAppV2Detail(r: Record<string, unknown>): ApplicationV2Detail {
  const fields: V2Bag = {};
  for (const [k, v] of Object.entries(r)) {
    if (!APP_V2_META_KEYS.has(k)) fields[k] = v as V2Value;
  }
  if (typeof fields.ownershipType === "string") fields.ownershipType = canonicalOwnershipType(fields.ownershipType);
  const principals = ((r.principals as V2Bag[]) ?? []).map((p) =>
    typeof p.title === "string" ? { ...p, title: canonicalOwnerTitle(p.title) } : p);
  return {
    id: r.id as string,
    status: (r.status as string) ?? "",
    wizardStep: (r.wizardStep as number) ?? 1,
    source: (r.source as string) ?? "",
    owner: r.owner ? toUserRef(r.owner) : userRef(r.userId as string | null),
    associate: r.associate ? toUserRef(r.associate) : userRef(r.associateUserId as string | null),
    merchantId: (r.merchantId as string | null) ?? null,
    createdAt: (r.createdAt as string) ?? (r.creationTime as string) ?? "",
    updatedAt: (r.updatedAt as string) ?? (r.lastModificationTime as string) ?? (r.createdAt as string) ?? (r.creationTime as string) ?? "",
    submittedAt: (r.submittedAt as string | null) ?? null,
    pricingRequired: r.pricingRequired !== false,
    statusSince: (r.statusSince as string | null) ?? null,
    processorMid: (r.processorMid as string | null) ?? null,
    underwritingNote: (r.underwritingNote as string | null) ?? null,
    underwritingRequested: r.underwritingRequested === true,
    viewerIsBackOffice: typeof r.viewerIsBackOffice === "boolean" ? r.viewerIsBackOffice : null,
    fields,
    principals,
    bankAccounts: (r.bankAccounts as V2Bag[]) ?? [],
    posSetup: (r.posSetup as V2Bag[]) ?? [],
    tradeReferences: (r.tradeReferences as V2Bag[]) ?? [],
    feeSchedule: (r.feeSchedule as V2Bag | null) ?? null,
    documents: (r.documents as ApplicationDocumentV2[]) ?? [],
  };
}

const APP_V2_SEARCH_COLS = ["dbaName", "legalName", "contactFirstName", "contactLastName", "email"];

export interface ApplicationActivityItem {
  at: string;
  kind: "status" | "pricing" | "owner" | "note" | "merge";
  by: string;
  fromStatus: string | null;
  toStatus: string | null;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
}

export interface ApplicationV2Summary {
  total: number;
  draft: number;
  submitted: number;
  awaitingAgentPricing?: number;
  needsReview?: number;
  underwriting?: number;
  eSignSent?: number;
  eSigned?: number;
  uwPended?: number;
  live?: number;
  waitingOnAgent?: number;
  inProgress?: number;
  viewerIsBackOffice?: boolean;
  approved: number;
  boarded: number;
  declined: number;
  cancelled: number;
}

export const NEEDS_REVIEW = "NeedsReview";
export const UNDERWRITING = "UnderwritingGroup";
export const WAITING_ON_AGENT = "WaitingOnAgentGroup";
export const IN_PROGRESS = "InProgressGroup";
export const CLAIMABLE = "ClaimableGroup";
export const SUBMITTED_GROUP = "SubmittedGroup";
export const ESIGNED_GROUP = "ESignedGroup";
export const DONE_GROUP = "DoneAtProcessorGroup";

const STATUS_GROUPS: Record<string, string[]> = {
  [NEEDS_REVIEW]: ["Submitted", "AwaitingAgentPricing", "New", "Pending"],
  [SUBMITTED_GROUP]: ["Submitted", "New", "Pending"],
  [ESIGNED_GROUP]: ["ESigned", "Underwriting"],
  [DONE_GROUP]: ["Approved", "Boarded", "Live"],
  [UNDERWRITING]: ["ESignSent", "ESigned", "ActionNeeded", "Underwriting"],
  [WAITING_ON_AGENT]: ["AwaitingAgentPricing", "ActionNeeded"],
  [IN_PROGRESS]: ["Submitted", "New", "Pending", "ESignSent", "ESigned", "Underwriting", "Approved", "Boarded"],
  [CLAIMABLE]: ["AwaitingAgentPricing", "Submitted", "New", "Pending", "ESignSent", "ESigned", "ActionNeeded", "Underwriting", "Approved"],
};

export type BankCheckAuditResult = "Match" | "ExtraDigits" | "Mismatch" | "Unreadable" | "Error";

export interface BankCheckAuditRow {
  applicationId: string;
  dbaName: string | null;
  status: string;
  result: BankCheckAuditResult;
  savedAccount: string | null;
  readAccount: string | null;
  routingMatches: boolean;
  problem: string | null;
}

export interface BankCheckAuditPage {
  total: number;
  next: number;
  rows: BankCheckAuditRow[];
}

export const applicationsV2 = {
  async list(params: PageParams = {}): Promise<Paged<ApplicationV2ListItem>> {
    const group = params.status ? STATUS_GROUPS[params.status] : undefined;
    const query = group
      ? { ...params, status: undefined, expr: [...(params.expr ?? []), odata.or(...group.map((s) => odata.eq("status", s)))] }
      : params;
    const res = await apiFetch<Paged<AppV2ListResponse> | AppV2ListResponse[]>(
      `/applications/v2${pageQuery(query, undefined, APP_V2_SEARCH_COLS)}`,
    );
    const p = toPaged<AppV2ListResponse>(res);
    return { ...p, items: p.items.map(toAppV2ListItem) };
  },
  async findByMerchant(merchantId: string): Promise<ApplicationV2ListItem | null> {
    const res = await this.list({ pageSize: 1, filter: { merchantId } });
    return res.items[0] ?? null;
  },
  summary() {
    return apiFetch<ApplicationV2Summary>("/applications/v2/summary");
  },
  async get(id: string): Promise<ApplicationV2Detail> {
    const r = await apiFetch<Record<string, unknown>>(
      `/applications/v2/${encodeURIComponent(id)}`,
    );
    return toAppV2Detail(r);
  },
  async create(): Promise<{ id: string }> {
    const res = await apiFetch<unknown>("/applications/v2", {
      method: "POST",
      body: JSON.stringify({ source: 0, userId: null, associateUserId: null }),
    });
    if (typeof res === "string") return { id: res };
    return { id: (res as { id?: string })?.id ?? "" };
  },
  save(id: string, req: SaveApplicationV2Request) {
    const { fields, ...rest } = req;
    const payload = { ...(fields ?? {}), ...rest };
    return apiFetch<OperationResult>(`/applications/v2/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },
  submit(id: string) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/submit`,
      { method: "POST" },
    );
  },

  requestRateException(id: string, reason: string) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/rate-exception`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
  },

  reassignOwner(id: string, userId: string) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/owner`,
      { method: "PUT", body: JSON.stringify({ userId }) },
    );
  },

  duplicates(id?: string) {
    return apiFetch<DuplicatePair[]>(id ? `/applications/v2/${encodeURIComponent(id)}/duplicates` : "/applications/v2/duplicates");
  },

  myDuplicates(id: string) {
    return apiFetch<DuplicatePair[]>(`/applications/v2/${encodeURIComponent(id)}/my-duplicates`);
  },

  answerDuplicate(id: string, otherId: string, same: boolean) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/duplicates/${encodeURIComponent(otherId)}/answer`,
      { method: "POST", body: JSON.stringify({ same }) },
    );
  },

  compare(a: string, b: string) {
    return apiFetch<ApplicationComparison>(`/applications/v2/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`);
  },

  merge(keepId: string, otherId: string) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(keepId)}/merge`,
      { method: "POST", body: JSON.stringify({ otherId }) },
    );
  },

  unmerge(id: string) {
    return apiFetch<OperationResult>(`/applications/v2/${encodeURIComponent(id)}/unmerge`, { method: "POST" });
  },

  bankCheckAudit(skip: number, take = 2) {
    return apiFetch<BankCheckAuditPage>(`/applications/v2/bank-check-audit?skip=${skip}&take=${take}`);
  },

  addMissingPricing(id: string, pricing: Record<string, unknown>) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/missing-pricing`,
      { method: "POST", body: JSON.stringify({ pricing }) },
    );
  },

  decideRateException(id: string, approve: boolean, note: string | null) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/rate-exception/decision`,
      { method: "POST", body: JSON.stringify({ approve, note }) },
    );
  },

  sendToProcessor(id: string, onlineApplication?: SaveApplicationV2Request) {
    let body = {};
    if (onlineApplication) {
      const { fields, ...rest } = onlineApplication;
      body = { onlineApplication: { ...(fields ?? {}), ...rest } };
    }
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/send-to-processor`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  moveStatus(id: string, status: string, expectedStatus: string) {
    return apiFetch<OperationResult>(
      `/applications/${encodeURIComponent(id)}/advance`,
      { method: "POST", body: JSON.stringify({ status, notes: null, expectedStatus }) },
    );
  },

  activity(id: string) {
    return apiFetch<ApplicationActivityItem[]>(`/applications/v2/${encodeURIComponent(id)}/activity`);
  },

  approve(id: string) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/approve`,
      { method: "POST" },
    );
  },

  board(id: string, mid: string | null = null) {
    return apiFetch<IdResponse>(
      `/applications/v2/${encodeURIComponent(id)}/board`,
      { method: "POST", body: JSON.stringify({ mid }) },
    );
  },

  markBoarded(id: string, mid: string) {
    return apiFetch<IdResponse>(
      `/applications/v2/${encodeURIComponent(id)}/mark-boarded`,
      { method: "POST", body: JSON.stringify({ mid }) },
    );
  },

  decline(id: string, reason: string | null) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/decline`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
  },

  cancel(id: string, reason: string | null) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/cancel`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
  },
  uploadDocument(id: string, file: File, documentType: string) {
    const form = new FormData();
    form.append("file", file);
    form.append("documentType", documentType);
    return apiUpload<void>(
      `/applications/v2/${encodeURIComponent(id)}/documents`,
      form,
    );
  },
  relabelDocument(id: string, docId: string, documentType: string) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/type`,
      { method: "PUT", body: JSON.stringify({ documentType }) },
    );
  },
  deleteDocument(id: string, docId: string) {
    return apiFetch<void>(
      `/applications/v2/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}`,
      { method: "DELETE" },
    );
  },

  documentFile(id: string, docId: string) {
    return apiDownloadNamed(
      `/applications/v2/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/download`,
    );
  },
  async downloadDocument(id: string, docId: string, filename: string) {
    const blob = await apiDownload(
      `/applications/v2/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/download`,
    );
    saveBlob(blob, filename);
  },
};

export interface CampaignListItem {
  id: string;
  name: string;
  status: string;
  campaignType: string;
  isPublic: boolean;
  acquirer: string | null;
  updatedAt: string;
}

export interface CampaignDetail {
  id: string;
  name: string;
  description: string | null;
  isPublic: boolean;
  campaignType: string;
  acquirer: string | null;
  boardingApplicationType: string;
  giactKbaEnabled: boolean;
  status: string;
  acquiringFeeSpecialNote: string | null;
  achFeeSpecialNote: string | null;
  tsysAssociation: string | null;
  leadSource: string | null;
  passThroughInterchangePlus: number | null;
  pinDebitRate: number | null;
  ebtRate: number | null;
  transactionFee: number | null;
  authorizationFee: number | null;
  avsFee: number | null;
  voiceAuthorizationFee: number | null;
  pinDebitAuthorizationFee: number | null;
  ebtAuthorizationFee: number | null;
  monthlyDashboardAccessFee: number | null;
  monthlyPciFee: number | null;
  monthlyMinimumFee: number | null;
  monthlyFee: number | null;
  applicationFee: number | null;
  annualFee: number | null;
  batchFee: number | null;
  chargebackFee: number | null;
  retrievalFee: number | null;
  qualifiedRate: number | null;
  midQualSurcharge: number | null;
  nonQualSurcharge: number | null;
  visaMcDiscoverRate: number | null;
  americanExpressOptBlueRate: number | null;
  offlineDebitRate: number | null;
  offlineDebitPassThrough: number | null;
  pinDebitRatePassThrough: number | null;
  gatewayFee: number | null;
  wirelessFee: number | null;
  posSystemFee: number | null;
  perTransFee: number | null;
  setupFee: number | null;
  serviceProviderMonthlyFee: number | null;
  otherServiceProviderFee: number | null;
  createdAt: string;
  updatedAt: string;
}

export const CAMPAIGN_TYPES = ["Acquiring", "Acquiring + ACH", "ACH only"] as const;

export const CAMPAIGN_STATUSES = ["Active", "Archived"] as const;

const APP_CAMPAIGN_SEARCH_COLS = ["name", "acquirer"];

export const applicationCampaigns = {
  async list(params: PageParams = {}): Promise<Paged<CampaignListItem>> {
    const res = await apiFetch<Paged<CampaignListResponse> | CampaignListResponse[]>(
      `/application-campaigns${pageQuery(params, undefined, APP_CAMPAIGN_SEARCH_COLS)}`,
    );
    const p = toPaged<CampaignListResponse>(res);
    return { ...p, items: p.items.map(toCampaignListItem) };
  },
  listAll(params: PageParams = {}): Promise<CampaignListItem[]> {
    return fetchAll((p) => this.list(p), params, "application-campaigns");
  },
  async get(id: string) {
    const r = await apiFetch<Record<string, unknown>>(`/application-campaigns/${encodeURIComponent(id)}`);
    return toCampaignDetail(r);
  },
  create(body: Partial<CampaignDetail> & { name: string }) {
    return apiFetch<IdResponse>("/application-campaigns", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  update(id: string, body: Partial<CampaignDetail> & { name: string }) {
    return apiFetch<OperationResult>(`/application-campaigns/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  remove(id: string) {
    return apiFetch<OperationResult>(`/application-campaigns/${encodeURIComponent(id)}/archive`, {
      method: "POST",
    });
  },
};

export interface ProcessorEmailItem {
  id: string;
  receivedAt: string;
  kind: "Unknown" | "ESigned" | "ActionNeeded" | "Approved" | "CodingComplete" | "FirstDeposit" | "UnderwritingReply" | "UnderwritingSent";
  mid: string | null;
  dbaName: string | null;
  additionalInfo: string | null;
  result: "Applied" | "Ignored" | "NeedsPerson" | "Flagged" | "Claimed" | "Resolved";
  note: string | null;
  applicationId: string | null;
  applicationName: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  fileCount?: number;
  suggestion?: ProcessorEmailSuggestion | null;
}

export interface DuplicateSide {
  id: string;
  name: string | null;
  status: string;
  submittedAt: string | null;
  agentName: string | null;
  documents: number;
}

export interface DuplicatePair {
  a: DuplicateSide;
  b: DuplicateSide;
  differenceCount: number;
  agentAnswer: "same" | "different" | null;
}

export interface ApplicationComparison {
  a: DuplicateSide;
  b: DuplicateSide;
  differences: { label: string; a: string | null; b: string | null }[];
}

export interface ProcessorEmailSuggestion {
  applicationId: string;
  name: string | null;
  ownerName: string | null;
  status: string;
}

export interface ProcessorEmailList {
  needsAttention: number;
  page: { items: ProcessorEmailItem[]; totalCount: number; pageNumber: number; pageSize: number };
}

export const processorEmails = {
  list(needsAttention: boolean, pageNumber = 1, pageSize = 50) {
    return apiFetch<ProcessorEmailList>(
      `/processor-emails?needsAttention=${needsAttention}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    );
  },
  claim(id: string, applicationId: string) {
    return apiFetch<OperationResult>(
      `/processor-emails/${encodeURIComponent(id)}/claim`,
      { method: "POST", body: JSON.stringify({ applicationId }) },
    );
  },
  resolve(id: string) {
    return apiFetch<OperationResult>(`/processor-emails/${encodeURIComponent(id)}/resolve`, { method: "POST" });
  },
  full(id: string) {
    return apiFetch<{ id: string; subject: string | null; from: string | null; receivedAt: string; markdown: string; fileCount?: number }>(
      `/processor-emails/${encodeURIComponent(id)}/full`,
    );
  },
};

export interface UnderwritingMeta {
  source: "processor-email" | "received" | "sent" | "sending" | "logged";
  email: string | null;
  subject: string | null;
  at: string;
  emailId?: string | null;
  senderName?: string | null;
  fromUnderwriting?: boolean;
}

export interface LogUnderwritingEmailInput {
  fromUnderwriting: boolean;
  senderName: string | null;
  senderEmail: string | null;
  sentAt: string;
  text: string;
}

export interface UnderwritingThread {
  conversationId: string;
  to: string | null;
  subject: string;
  open: boolean;
  meta: Record<string, UnderwritingMeta>;
  cc?: string | null;
}

export const underwriting = {
  get(appId: string) {
    return apiFetch<UnderwritingThread>(`/applications/v2/${encodeURIComponent(appId)}/underwriting`);
  },
  setRecipient(appId: string, email: string) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(appId)}/underwriting/recipient`,
      { method: "PUT", body: JSON.stringify({ email }) },
    );
  },
  deliver(appId: string, commentId: string) {
    return apiFetch<OperationResult>(
      `/applications/v2/${encodeURIComponent(appId)}/underwriting/messages/${encodeURIComponent(commentId)}/deliver`,
      { method: "POST" },
    );
  },
  log(appId: string, input: LogUnderwritingEmailInput, files: File[]) {
    const form = new FormData();
    form.append("fromUnderwriting", String(input.fromUnderwriting));
    if (input.senderName) form.append("senderName", input.senderName);
    if (input.senderEmail) form.append("senderEmail", input.senderEmail);
    form.append("sentAt", input.sentAt);
    form.append("text", input.text);
    for (const f of files) form.append("files", f, f.name);
    return apiUpload<string>(`/applications/v2/${encodeURIComponent(appId)}/underwriting/log`, form);
  },
};
